<template>
  <div class="v-step" :id="`v-step-${hash}`" :ref="`v-step-${hash}`" :class="{'invisible': false}">
    <slot name="header">
      <div v-if="step.header" class="v-step__header">
        <div v-if="step.header.title" v-html="step.header.title"></div>
      </div>
    </slot>

    <slot name="content">
      <div class="v-step__content">
        <div v-if="step.content" v-html="step.content"></div>
        <div v-else>This is a demo step! The id of this step is {{ hash }} and it targets {{ step.target }}.</div>
      </div>
    </slot>

    <slot name="actions">
      <div class="v-step__buttons">
        <button @click.prevent="stop" v-if="!isLast" class="v-step__button">Skip tour</button>
        <button @click.prevent="previousStep" v-if="!isFirst" class="v-step__button">Previous</button>
        <button @click.prevent="nextStep" v-if="!isLast" class="v-step__button">Next</button>
        <button @click.prevent="stop" v-if="isLast" class="v-step__button">OK</button>
      </div>
    </slot>

    <div class="v-step__arrow" :class="{ 'v-step__arrow--dark': step.header && step.header.title }"></div>
  </div>
</template>

<script>
  import Popper from 'popper.js';
  import sum from 'hash-sum';
  import {DEFAULT_STEP_OPTIONS} from './constants';

  export default {
    name: 'v-step',
    props: {
      step: Object,
      previousStep: Function,
      nextStep: Function,
      stop: Function,
      isFirst: Boolean,
      isLast: Boolean,
    },
    data() {
      return {
        hash: sum(this.step.target),
        popper: null,
      };
    },
    computed: {
      params() {
        return {
          ...DEFAULT_STEP_OPTIONS,
          ...this.step.params,
        };
      },
    },
    methods: {
      createStep() {
        const targetElement = document.querySelector(this.step.target);
        // console.log(`[Vue Tour] The target element ${this.step.target} of .v-step[id="${this.hash}"] is:`, targetElement);
        if (targetElement) {
          if (this.params.scroll) {
            targetElement.scrollIntoView(this.params.scroll);
          }
          this.popper = new Popper(targetElement, this.$refs[`v-step-${this.hash}`], this.params);
        } else {
          console.error(`[Vue Tour] The target element ${this.step.target} of .v-step[id="${this.hash}"] does not exist!`);
          this.$emit('targetNotFound', this.step);
        }
      },
    },
    mounted() {
      this.createStep();
    },
  };
</script>

<style lang="scss" scoped>
  // default color
  // $tour-color:  #50596c !default;
  $tour-color: #6f42c1 !default;
  $tour-color-dark: darken($tour-color, 10%) !default;

  .v-step {
    background: $tour-color;
    color: white;
    max-width: 320px;
    border-radius: 0;
    filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.5));
    padding: 1rem;
    text-align: center;
    z-index: 1021;
  }

  .v-step .v-step__arrow {
    width: 0;
    height: 0;
    border-style: solid;
    position: absolute;
    margin: 0.5rem;
    z-index: 1021;
  }

  .v-step .v-step__arrow {
    border-color: $tour-color;

    &--dark {
      border-color: $tour-color-dark;
    }
  }

  .v-step[x-placement^="top"] {
    margin-bottom: 0.5rem;
  }

  .v-step[x-placement^="top"] .v-step__arrow {
    border-width: 0.5rem 0.5rem 0 0.5rem;
    border-left-color: transparent;
    border-right-color: transparent;
    border-bottom-color: transparent;
    bottom: -0.5rem;
    left: calc(50% - 1rem);
    margin-top: 0;
    margin-bottom: 0;
  }

  .v-step[x-placement^="bottom"] {
    margin-top: 0.5rem;
  }

  .v-step[x-placement^="bottom"] .v-step__arrow {
    border-width: 0 0.5rem 0.5rem 0.5rem;
    border-left-color: transparent;
    border-right-color: transparent;
    border-top-color: transparent;
    top: -0.5rem;
    left: calc(50% - 1rem);
    margin-top: 0;
    margin-bottom: 0;
  }

  .v-step[x-placement^="right"] {
    margin-left: 0.5rem;
  }

  .v-step[x-placement^="right"] .v-step__arrow {
    border-width: 0.5rem 0.5rem 0.5rem 0;
    border-left-color: transparent;
    border-top-color: transparent;
    border-bottom-color: transparent;
    left: -0.5rem;
    top: calc(50% - 1rem);
    margin-left: 0;
    margin-right: 0;
  }

  .v-step[x-placement^="left"] {
    margin-right: 0.5rem;
  }

  .v-step[x-placement^="left"] .v-step__arrow {
    border-width: 0.5rem 0 0.5rem 0.5rem;
    border-top-color: transparent;
    border-right-color: transparent;
    border-bottom-color: transparent;
    right: -0.5rem;
    top: calc(50% - 1rem);
    margin-left: 0;
    margin-right: 0;
  }

  /* Custom */

  .v-step__header {
    margin: -1rem -1rem 0.5rem;
    padding: 0.5rem;
    background-color: $tour-color-dark;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  .v-step__content {
    margin: 0 0 1rem 0;
  }

  .v-step__button {
    background: transparent;
    border: .05rem solid white;
    border-radius: 0;
    color: white;
    cursor: pointer;
    display: inline-block;
    font-size: .8rem;
    height: 1.8rem;
    line-height: 1rem;
    outline: none;
    padding: .35rem .4rem;
    text-align: center;
    text-decoration: none;
    transition: all .2s ease;
    vertical-align: middle;
    white-space: nowrap;
    margin-left: 0.25rem;
    margin-right: 0.25rem;

    &:hover {
      background-color: rgba(white, 0.95);
      color: $tour-color;
    }
  }
</style>
